<template>
  <div v-if="!hasBookedSeatsInSelection">
    <div class="font-bold p-2 flex">POS Allocation</div>
    <SMList v-if="!otherElementsThanSeatsAndGa" class="custom-list">
      <template v-if="selectedOnlyOneGa">
        <template v-if="categorySelectedForGaOrOther">
          <SMListItem
            v-for="(posItem, index) in pos"
            :key="index"
            class="flex gap-2 bg-white hover:bg-gray-100 cursor-pointer border items-center"
          >
            <div class="color-button" :style="{ 'background-color': posItem.color }" />

            <span class="pos-name" :title="pos.name">{{ posItem.name }}</span>
            <SMNumberInput
              v-model="gaPos[posItem.id]"
              :disabled="checkIfDisabled(posItem.id)"
              :min="0"
              :max="getMaxValForPos(posItem.id)"
              @update:modelValue="gaPosChange(posItem, $event)"
            />
            <span>/{{ gaCapacity }}</span>
          </SMListItem>
        </template>
        <SMListItem v-if="!pos || pos.length === 0" class="flex gap-2">
          <SMIcon icon="warning" />
          <span>There are no points of sale available. Please add some first.</span>
        </SMListItem>
        <SMListItem v-if="pos.length > 0 && !categorySelectedForGaOrOther">
          Please assign a category to this GA first
        </SMListItem>

        <button
          v-if="pos && pos.length > 0"
          class="flex gap-2 items-center m-2 border rounded-sm p-2 hover:bg-gray-100 cursor-pointer bg-white"
          @click="clearAllPOS"
        >
          <img src="@/assets/img/deleteall.png" alt="Delete" width="32px" height="32px" />
          <span>Clear all POS</span>
        </button>
      </template>
      <template v-else-if="selectedOtherThanGa">
        <template v-for="(pos, index) in pos">
          <SMListItem
            v-if="isCurrentPos(pos.id) && categorySelectedForGaOrOther"
            :key="index"
            class="flex gap-2 bg-white hover:bg-gray-100 cursor-pointer border items-center"
            :class="{ selected: pos === selectedPos }"
            @click="togglePos(pos)"
          >
            <div class="color-button" :style="{ 'background-color': pos.color }" />
            <span class="pos-name" :title="pos.name">{{ pos.name }}</span>
          </SMListItem>
        </template>
        <template v-for="(pos, index1) in pos">
          <SMListItem
            v-if="!isCurrentPos(pos.id) && categorySelectedForGaOrOther"
            :key="index1"
            :class="{ selected: pos === selectedPos }"
            class="flex gap-2 bg-white hover:bg-gray-100 cursor-pointer border items-center"
            @click="togglePos(pos)"
          >
            <div class="color-button" :style="{ 'background-color': pos.color }"></div>

            <span class="pos-name" :title="pos.name">{{ pos.name }}</span>
          </SMListItem>
        </template>
        <SMListItem v-if="!pos || pos.length === 0" class="flex gap-2">
          <SMIcon icon="warning" />
          <span>There are no points of sale available. Please add some first.</span>
        </SMListItem>
        <SMListItem v-if="pos.length > 0 && !categorySelectedForGaOrOther">
          Please assign a category to the selected seats first
        </SMListItem>

        <SMButton
          v-if="pos && pos.length > 0 && categorySelectedForGaOrOther"
          icon="deleteall"
          type="primary"
          filled
          class="m-2"
          @click="clearAllPOS"
        >
          Clear all POS
        </SMButton>
      </template>
    </SMList>
  </div>
</template>

<script lang="ts">
import eventbus from 'tiny-emitter/instance';
import { mapState, mapActions } from 'vuex';
import forOwn from 'lodash/forOwn';
import sumBy from 'lodash/sumBy';
import first from 'lodash/first';
import { TYPE, SEATSTATUS } from '@/constants';
import SMIcon from '@/components/ui/SMIcon.vue';
import SMInput from '@/components/ui/SMInput.vue';
import SMNumberInput from '@/components/ui/SMNumberInput.vue';
import SMButton from '@/components/ui/SMButton.vue';

export default {
  props: ['selection'],
  data() {
    return {
      selectedPos: null,
      gaPos: [],
      asignposlength: 0,
      currentPos: [],
      firstLoad: true,
    };
  },
  computed: {
    ...mapState('pos', ['pos']),
    selectedOnlyOneGa() {
      return (
        this.selection.items.length === 1 && this.selection.items[0].type === TYPE.GA && this.$app.props.isManageGA
      );
    },
    selectedOtherThanGa() {
      return !this.selection.items.find((u) => u.type === TYPE.GA);
    },
    categorySelectedForGaOrOther() {
      if (this.selectedOnlyOneGa) {
        const selectedGaAttr = this.selection.items[0].attributes;
        return selectedGaAttr.categoryId !== undefined && selectedGaAttr.categoryId !== '';
      } else if (this.selectedOtherThanGa) {
        let hasCategoriesAssigned = true;
        this.selection.items.forEach((item) => {
          let fitem = item;
          if (item.type === TYPE.SEAT || item.type === TYPE.TABLESEAT || item.type === TYPE.TABLE) {
            if (item.type === TYPE.TABLE) {
              fitem = first(item.seats);
            }
          }
          if (fitem.category === undefined || fitem.category === '') {
            hasCategoriesAssigned = false;
          }
        });
        return hasCategoriesAssigned;
      }
      return true;
    },
    gaCapacity() {
      let gaCapacity = 0;
      if (this.selection.items && this.selection.items.length === 1) {
        const cat = this.$app.getCategoryObj(this.selection.items[0].attributes.categoryId);
        gaCapacity = cat.capacity;
        gaCapacity -= this.gaBlocked;
      }
      return gaCapacity;
    },
    gaBlocked() {
      return this.selection.items[0].attributes && this.selection.items[0].attributes.blocked
        ? parseInt(this.selection.items[0].attributes.blocked, 10)
        : 0;
    },
    availablePos() {
      return this.gaCapacity - sumBy(this.gaPos, (gp) => (!gp ? 0 : +gp));
    },
    otherElementsThanSeatsAndGa() {
      let otherElementsSelected = false;
      this.selection.items.forEach((item) => {
        if (
          item.type !== TYPE.GA &&
          item.type !== TYPE.TABLE &&
          item.type !== TYPE.SEAT &&
          item.type !== TYPE.TABLESEAT
        ) {
          otherElementsSelected = true;
        }
      });
      return otherElementsSelected;
    },
    hasBookedSeatsInSelection() {
      let bookedUnits = 0;
      if (this.selection.items.length > 0) {
        this.selection.items.forEach((u) => {
          if (u.type === TYPE.SEAT || u.type === TYPE.TABLESEAT) {
            bookedUnits +=
              u.attributes.status === SEATSTATUS.OCCUPIED || u.attributes.status === SEATSTATUS.RESERVED || u.inCart
                ? 1
                : 0;
          } else if (u.type === TYPE.TABLE) {
            const bookedTableSeats = u.seats.filter(
              (s) =>
                s.attributes.status === SEATSTATUS.OCCUPIED || s.attributes.status === SEATSTATUS.RESERVED || s.inCart
            );
            bookedUnits += bookedTableSeats.length > 0 ? 1 : 0;
          }
        });
      }
      return !!bookedUnits;
    },
  },
  watch: {
    pos(val) {
      if (!this.selectedOnlyOneGa) {
        return;
      }
      val.forEach((v) => {
        const { pos } = this.selection.items[0];
        this.gaPos[v.id] = pos[v.id] || 0;
      });
    },
  },
  created() {
    this.$app.refreshPosList();
    this.asignposlength = this.pos.length;
    if (this.selectedOnlyOneGa) {
      const { pos } = this.selection.items[0];
      forOwn(pos, (val, key) => {
        if (!val) {
          return;
        }
        this.gaPos[key] = val || 0;
      });
    } else if (this.selectedOtherThanGa && !this.otherElementsThanSeatsAndGa) {
      this.currentPos = [];
      this.selection.items.forEach((item) => {
        let itemPos = null;
        if (item.type === TYPE.SEAT || item.type === TYPE.TABLESEAT) {
          itemPos = parseInt(item.attributes.posId, 10);
        } else if (item.type === TYPE.TABLE) {
          const fitem = first(item.seats);
          itemPos = parseInt(fitem.attributes.posId, 10);
        } else {
          itemPos = parseInt(item.attributes.posId, 10);
        }
        if (itemPos !== undefined && typeof itemPos !== 'number') {
          itemPos = itemPos.id;
        }
        if (itemPos && this.currentPos.indexOf(itemPos) === -1) {
          this.currentPos.push(itemPos);
        }
      });
    }
  },
  methods: {
    ...mapActions('pos', ['loadPosList']),
    checkIfDisabled(posId) {
      return !!(
        (this.gaPos[posId] === undefined || this.gaPos[posId] === '' || this.gaPos[posId] === 0) &&
        this.getMaxValForPos(posId) === 0
      );
    },
    isCurrentPos(posId) {
      return this.currentPos.find((e) => parseInt(e, 10) === parseInt(posId, 10));
    },
    gaPosChange(posItem, val) {
      this.firstLoad = false;
      val = parseInt(val, 10);
      const maxVal = this.getMaxValForPos(posItem.id);
      if (maxVal <= 0) val = 0;
      if (Number.isNaN(val) || val < 0) {
        this.gaPos[posItem.id] = 0;
      } else if (maxVal >= val) {
        if (val > 0) {
          this.gaPos[posItem.id] = val;
        } else {
          delete this.gaPos[posItem.id];
        }
        this.selection.items[0].pos = this.gaPos;
      } else {
        this.gaPos[posItem.id] = maxVal;
      }
      eventbus.emit('pos:ga-updated');
      eventbus.emit('history:snapshot', `POS assigned: ${posItem.name}`);
    },
    clearAllPOS() {
      this.selection.items.forEach((item) => {
        if (item.type !== TYPE.TABLE) {
          item.pointOfSale = null;
          item.pos = null;
        } else {
          item.seats.forEach((u) => {
            u.pointOfSale = null;
            u.pos = null;
          });
        }
        this.gaPos = [];
      });
      this.selectedPos = null;
      eventbus.emit('seatmap:pos:updated');
      eventbus.emit('history:snapshot', 'Cleared POS assignment');
    },
    getMaxValForPos(posId) {
      let maxVal = parseInt(this.availablePos, 10);
      if (this.gaPos[posId]) {
        maxVal += parseInt(this.gaPos[posId], 10);
      }
      if (maxVal < 0) maxVal = 0;
      return maxVal;
    },
    togglePos(pos) {
      if (this.selectedPos === pos) {
        this.selectedPos = null;
        return;
      }
      this.selectedPos = pos;
      eventbus.emit('pos:set', this.selectedPos);
      eventbus.emit('history:snapshot', `POS assigned: ${pos.name}`);
    },
  },
  components: { SMIcon, SMInput, SMNumberInput, SMButton },
};
</script>

<style lang="scss" scoped>
.md-list-text-container {
  display: flex !important;
  flex-direction: row;
  flex-flow: row;
  justify-content: flex-start;
  width: 200px;
  > span {
    align-self: center;
  }
  .align-right {
    margin-left: auto;
  }
}

.pos-name {
  max-width: 200px;
}

.selected {
  background-color: #4051b4 !important;
  span {
    color: white !important;
  }
}
.sub-list-container .md-divider {
  bottom: auto !important;
}
.md-avatar {
  overflow: visible !important;
}
.color-button {
  min-width: 32px;
  min-height: 32px;
}

.pading-left {
  padding-left: 5px;
}

.no-padding {
  padding: 0 !important;
}

.md-list-item:not(.sub-list-container) {
  cursor: pointer;
  padding: 2px 0;
  border-bottom: 1px solid #eae8e8;
  &:first-child {
    padding-top: 2px;
  }
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: #eaeaea;
  }
}

.md-list.md-dense {
  padding: 0 0 !important;
}
.pos-icon {
  width: 20px;
  &.clickable {
    opacity: 0.4;
    &:hover {
      opacity: 1;
    }
  }
}
.md-input-container {
  min-height: auto !important;
  margin: 0 !important;
  padding-top: 0;
  width: 165px;
  display: inline-block !important;
  vertical-align: middle;
  &.numeric-input {
    width: 50px !important;
    min-width: 50px !important;
    margin-left: auto !important;
    margin-right: 5px !important;
  }
}
</style>
